import { Head } from "@inertiajs/react";
import PublicLayout from "@/components/public/PublicLayout";
import DonationCard from "@/components/public/DonationCard";
import StickyDonateBar from "@/components/public/StickyDonateBar";
import SupportersList from "@/components/public/SupportersList";
import SchoolProgress from "@/components/public/SchoolProgress";
import MilestonesCard from "@/components/schools/MilestonesCard";
import { formatDateRange } from "@/lib/format";

/**
 * The school's public donation page, for gifts to the whole school
 * rather than one student. Shown while a campaign is running, and before
 * reading starts once its donations have opened.
 */
export default function Show({
  school, campaign, milestones, donations, donate_path,
  donation_state, donations_open_on, fee_schedule, square, pretend_payments,
}) {
  const open = donation_state === "open";
  const wrappingUp = campaign.state === "grace";
  const badge = { draft: "Donations open", grace: "Wrapping up" }[campaign.state] || "Live now";

  return (
    <PublicLayout doodles mainClassName={`max-w-5xl py-10 md:py-14 ${open ? "pb-28 lg:pb-14" : ""}`}>
      <Head title={`${school.name} read-a-thon`} />

      <div className="grid gap-8 lg:grid-cols-[1.3fr_1fr] lg:items-start">
        <div className="min-w-0 space-y-6">
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <span className={`rounded-full px-2.5 py-1 font-semibold uppercase tracking-wider ${wrappingUp ? "bg-warning/15 text-warning-foreground" : "bg-primary/10 text-primary"}`}>
                {badge}
              </span>
              <span>{formatDateRange(campaign.start_at, campaign.end_at)}</span>
            </div>
            <h1 className="font-display text-3xl font-semibold leading-tight text-foreground md:text-4xl">{school.name}</h1>
            <p className="leading-relaxed text-muted-foreground">
              {campaign.name}
              {(school.city || school.province) && <> · {[school.city, school.province].filter(Boolean).join(", ")}</>}
            </p>
            {campaign.description && <p className="leading-relaxed text-foreground">{campaign.description}</p>}
          </div>

          <SchoolProgress
            school={school}
            campaign={campaign}
            raisedCents={campaign.raised_cents}
            minutesLogged={campaign.minutes_logged}
            count={campaign.donor_count}
            countWord="donor"
            title="How the school is doing"
          />

          {donations.length > 0 && (
            <section className="space-y-2">
              <h2 className="font-sans text-xs font-semibold uppercase tracking-wider text-muted-foreground">Recent supporters</h2>
              <SupportersList donations={donations} />
            </section>
          )}
        </div>

        <div className="space-y-4 lg:sticky lg:top-6">
          <DonationCard
            school={school}
            donatePath={donate_path}
            donationState={donation_state}
            donationsOpenOn={donations_open_on}
            feeSchedule={fee_schedule}
            square={square}
            pretendPayments={pretend_payments}
            title={`Donate to ${school.name}`}
            description="Your donation supports the whole school, not one student."
            messagePlaceholder="Go readers!"
          />
        </div>
      </div>

      {milestones.length > 0 && (
        <div className="mt-14">
          <MilestonesCard milestones={milestones} manage_path={null} title="School rewards" />
        </div>
      )}

      {open && <StickyDonateBar label={`Donate to ${school.name}`} />}
    </PublicLayout>
  );
}

// Errors show inline next to the form, not as a toast too.
Show.inlineFlashAlerts = true;
